import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import TrackcastHero, {
  DEVICE_CONTACT_URL,
} from "@/components/trackcast/TrackcastHero";
import LatencyBoard from "@/components/trackcast/LatencyBoard";

const APP_SPEC_KEYS = ["camera", "latency", "recording", "platform"] as const;
const DEVICE_SPEC_KEYS = [
  "cameras",
  "latency",
  "night",
  "recording",
  "control",
] as const;
const STEP_KEYS = ["step1", "step2", "step3"] as const;

interface TierCardProps {
  tier: "app" | "device";
  specKeys: readonly string[];
  chipClass: string;
  cta: { className: string; href?: string; to?: string };
  badgeKey?: string;
}

function TierCard({ tier, specKeys, chipClass, cta, badgeKey }: TierCardProps) {
  const { t } = useTranslation("pages");
  return (
    <article
      id={`trackcast-${tier}`}
      className="bg-white border border-secondary-200 rounded-xl p-6 flex flex-col gap-4"
    >
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-lg font-bold tracking-tight flex items-center gap-2 flex-wrap">
          {t(`trackcast.tiers.${tier}.name`)}
          {badgeKey && (
            <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-amber-700 whitespace-nowrap">
              {t(badgeKey)}
            </span>
          )}
        </h4>
        <span
          className={`font-mono text-xs font-semibold tabular-nums whitespace-nowrap rounded-full px-2.5 py-1 ${chipClass}`}
        >
          {t(`trackcast.tiers.${tier}.latencyChip`)}
        </span>
      </div>
      <p className="text-sm text-secondary-500">
        {t(`trackcast.tiers.${tier}.description`)}
      </p>
      <dl className="grid grid-cols-[104px_1fr] gap-x-3.5 gap-y-2 text-sm">
        {specKeys.map((key) => (
          <div key={key} className="contents">
            <dt className="pt-0.5 font-mono text-[10.5px] tracking-[0.12em] uppercase text-secondary-400">
              {t(`trackcast.tiers.${tier}.specs.${key}.label`)}
            </dt>
            <dd className="text-secondary-700">
              {t(`trackcast.tiers.${tier}.specs.${key}.value`)}
            </dd>
          </div>
        ))}
      </dl>
      {cta.to ? (
        <Link to={cta.to} className={`mt-auto ${cta.className}`}>
          {t(`trackcast.tiers.${tier}.cta`)}
        </Link>
      ) : (
        <a href={cta.href} className={`mt-auto ${cta.className}`}>
          {t(`trackcast.tiers.${tier}.cta`)}
        </a>
      )}
      <p className="text-center text-xs text-secondary-400">
        {t(`trackcast.tiers.${tier}.note`)}
      </p>
    </article>
  );
}

export default function TrackcastPage() {
  const { t } = useTranslation("pages");

  return (
    <div className="page-container pb-16">
      <div className="mb-6">
        <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-secondary-500 mb-1.5">
          {t("trackcast.eyebrow")}
        </p>
        <h1 className="page-title">{t("trackcast.title")}</h1>
        <p className="page-subtitle">{t("trackcast.subtitle")}</p>
      </div>

      <TrackcastHero />

      <div className="mt-14">
        <LatencyBoard />
      </div>

      <section className="mt-14">
        <p className="section-title">{t("trackcast.tiers.sectionTitle")}</p>
        <h3 className="text-xl font-bold tracking-tight mb-5">
          {t("trackcast.tiers.lead")}
        </h3>
        <div className="grid gap-5 md:grid-cols-2">
          <TierCard
            tier="app"
            specKeys={APP_SPEC_KEYS}
            chipClass="bg-primary-50 text-primary-700"
            cta={{ to: "/trackcast/android", className: "btn-primary" }}
          />
          <TierCard
            tier="device"
            specKeys={DEVICE_SPEC_KEYS}
            chipClass="bg-violet-50 text-violet-600"
            cta={{ href: DEVICE_CONTACT_URL, className: "btn-secondary" }}
            badgeKey="trackcast.tiers.device.badge"
          />
        </div>
      </section>

      <section className="mt-14">
        <p className="section-title">{t("trackcast.how.sectionTitle")}</p>
        <h3 className="text-xl font-bold tracking-tight mb-5">
          {t("trackcast.how.lead")}
        </h3>
        <div className="grid gap-5 md:grid-cols-3">
          {STEP_KEYS.map((step) => (
            <div
              key={step}
              className="bg-white border border-secondary-200 rounded-xl p-5"
            >
              <span className="font-mono text-[11px] font-semibold tracking-[0.15em] uppercase text-primary-600">
                {t(`trackcast.how.${step}Label`)}
              </span>
              <h4 className="mt-2.5 mb-1.5 font-bold">
                {t(`trackcast.how.${step}Title`)}
              </h4>
              <p className="text-sm text-secondary-500">
                {t(`trackcast.how.${step}Description`)}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-5 rounded-xl border border-primary-100 bg-primary-50 px-5 py-4 text-sm text-secondary-700">
          <strong className="text-secondary-900">
            {t("trackcast.how.calloutTitle")}
          </strong>{" "}
          {t("trackcast.how.calloutBody")}
        </div>
      </section>

      <section className="mt-14">
        <div className="bg-white border border-secondary-200 rounded-xl p-6 flex flex-wrap items-center justify-between gap-6">
          <div>
            <h3 className="text-lg font-bold tracking-tight mb-1.5">
              {t("trackcast.integration.title")}
            </h3>
            <p className="text-sm text-secondary-500 max-w-xl">
              {t("trackcast.integration.body")}
            </p>
          </div>
          <Link to="/trackcast/android" className="btn-primary px-5">
            {t("trackcast.integration.cta")}
          </Link>
        </div>
      </section>
    </div>
  );
}
